import { useState, useMemo, useRef, useCallback } from "react";
import Select from "react-select";
import { t } from "i18next";
import PrimaryButton from "components/Form/Buttons/PrimaryButton";
import RadioButton from "components/Form/RadioButton";
import { useRuleDataContext } from "../../context/RuleDataProvider";
import useDynamicFieldConfig from "../../hooks/useDynamicFieldConfig";

const RuleMessageOptionContent = ({
  id,
  isDynamicMessageConstruction,
  configList,
  placeholder,
  viewOnly,
  staticMessage,
  dynamicMessage,
  isDynamicMessageType,
  onMessageAttributeChange,
  isActive,
  children = <></>,
}) => {
  const [fieldSelection, setFieldSelection] = useState("");
  const dynamicFieldRef = useRef(null);
  const { query, groupData } = useRuleDataContext();
  const allowedFieldIds = useMemo(() => {
    const groupIds = [];
    query.forEach((q) => {
      groupIds.push(q.group);
      if (q.valueFieldGroup) {
        groupIds.push(Number(q.valueFieldGroup));
      }
    });
    return groupData.reduce((acc, field) => {
      if (groupIds.includes(field.groupId)) {
        return [...acc, field.customFieldsId];
      } else {
        return [...acc];
      }
    }, []);
  }, [query, groupData]);
  const groupFilter = useCallback(
    (group) => allowedFieldIds?.find((e) => e == group.customFieldsId),
    [allowedFieldIds]
  );

  const { filteredGroupFields, selectedGroup, setSelectedGroup } =
    useDynamicFieldConfig(groupData, configList, groupFilter);
  const finalColumnList = useMemo(
    () =>
      filteredGroupFields.find((e) => "" + e.value === "" + selectedGroup)
        ?.subFields ?? [][selectedGroup],
    [filteredGroupFields, selectedGroup]
  );

  const addDynamicQueryMessage = () => {
    const cursorPos = dynamicFieldRef.current.selectionStart;
    const foundedData = groupData.find(
      (e) => "" + e.customFieldsId === "" + fieldSelection
    );
    if (foundedData) {
      onMessageAttributeChange(
        "dynamicMessage",
        `${dynamicMessage?.slice(0, cursorPos) ?? ""} {${
          foundedData.queryString
        }} ${dynamicMessage?.slice(cursorPos) ?? ""}`
      );
    }
  };

  return (
    <div
      className={"tab-pane fade" + (isActive ? " show active" : "")}
      id="internal"
      role="tabpanel"
      aria-labelledby="internal-tab"
    >
      <div className="col-lg-8 col-md-8">
        {children}
        {isDynamicMessageConstruction && (
          <div className="mb-3">
            <label className="form-label">
              {t("createRuleEngine.settingsTab.messageOptions.messageType")}
            </label>
            <div>
              <RadioButton
                id={"staticMessageSelection" + (id ?? "")}
                label={t("createRuleEngine.settingsTab.messageOptions.static")}
                isChecked={!isDynamicMessageType}
                onChange={(e) => {
                  onMessageAttributeChange(
                    "isDynamicMessageType",
                    !e.target.checked
                  );
                }}
              />
              <RadioButton
                id={"dynamicMessageSelection" + (id ?? "")}
                label={t("createRuleEngine.settingsTab.messageOptions.dynamic")}
                isChecked={isDynamicMessageType}
                onChange={(e) => {
                  onMessageAttributeChange(
                    "isDynamicMessageType",
                    e.target.checked
                  );
                }}
              />
            </div>
          </div>
        )}

        {isDynamicMessageType ? (
          <div>
            <span
              className="text-danger mb-3 d-block"
              style={{ fontSize: "14px", fontWeight: "bold" }}
            >
              {t(
                "createRuleEngine.settingsTab.messageOptions.dynamicMessageNote"
              )}
            </span>
            <div className="d-flex gap-3 mb-3">
              <div className="w-100">
                <Select
                  options={filteredGroupFields}
                  placeholder="Group select"
                  onChange={(options) => {
                    setSelectedGroup(options.value);
                  }}
                  classNamePrefix={"select"}
                  menuPosition={"fixed"}
                  isDisabled={viewOnly}
                />
              </div>
              <div className="w-100">
                <Select
                  options={finalColumnList}
                  placeholder="Field select"
                  onChange={(options) => {
                    setFieldSelection(options.value);
                  }}
                  classNamePrefix={"select"}
                  menuPosition={"fixed"}
                  isDisabled={viewOnly}
                />
              </div>
              <PrimaryButton
                title={"Add"}
                onClick={addDynamicQueryMessage}
                disabled={viewOnly}
              />
            </div>
            <textarea
              style={{
                minHeight: "120px",
              }}
              ref={dynamicFieldRef}
              name={`dynamic-field-${id}`}
              className="form-control"
              rows={5}
              disabled={viewOnly}
              placeholder={placeholder}
              value={dynamicMessage}
              onChange={(e) => {
                onMessageAttributeChange("dynamicMessage", e.target.value);
              }}
            />
          </div>
        ) : (
          <div>
            <textarea
              style={{
                minHeight: "120px",
              }}
              name=""
              className="form-control"
              rows={5}
              disabled={viewOnly}
              placeholder={placeholder}
              value={staticMessage}
              onChange={(e) => {
                onMessageAttributeChange("message", e.target.value);
              }}
            ></textarea>
          </div>
        )}
      </div>
    </div>
  );
};

export default RuleMessageOptionContent;
